// Copyright (c) 2026 Interpaste, all rights reserved
// @author: norelock <https://gitlab.com/mvvnlight>

type VMTag = keyof HTMLElementTagNameMap

export interface VMNodeSpec<T extends VMTag = VMTag> {
  tag: T
  key?: string
  parentKey?: string
  id?: string
  className?: string
  text?: string
  attrs?: Record<string, string>
  dataset?: Record<string, string>
  style?: Record<string, string>
  children?: VMNodeSpec[]
}

/**
 * Tiny DOM VM for declarative, keyed element creation.
 * Keys make nodes stable across re-renders and prevent DOM thrash.
 */
export class HtmlVM {
  private keyed = new Map<string, HTMLElement>()

  constructor(private root: HTMLElement) { }

  create<T extends VMTag>(spec: VMNodeSpec<T>, parent: HTMLElement = this.root): HTMLElementTagNameMap[T] {
    if (spec.parentKey) {
      parent = this.get(spec.parentKey) ?? parent
    }

    const node = this.resolveNode(spec)
    this.applySpec(node, spec)

    if (node.parentElement !== parent) {
      parent.appendChild(node)
    }

    if (spec.children) {
      for (const child of spec.children) {
        this.create(child, node)
      }
    }

    return node as HTMLElementTagNameMap[T]
  }

  get(key: string): HTMLElement | null {
    return this.keyed.get(key) ?? null
  }

  remove(key: string): void {
    const node = this.keyed.get(key)
    if (!node) return

    node.remove()
    this.keyed.delete(key)
  }

  clear(parent: HTMLElement = this.root): void {
    parent.replaceChildren()
  }

  destroy(): void {
    this.keyed.clear()
  }

  private resolveNode<T extends VMTag>(spec: VMNodeSpec<T>): HTMLElement {
    if (!spec.key) return document.createElement(spec.tag)

    const existing = this.keyed.get(spec.key)
    if (existing && existing.tagName.toLowerCase() === spec.tag) {
      return existing
    }

    const node = document.createElement(spec.tag)
    this.keyed.set(spec.key, node)
    return node
  }

  private applySpec(node: HTMLElement, spec: VMNodeSpec): void {
    node.className = spec.className ?? ''

    if (spec.id) node.id = spec.id
    else node.removeAttribute('id')

    if (spec.text != null) node.textContent = spec.text

    this.applyDataset(node, spec.dataset)
    this.applyAttrs(node, spec.attrs)
    this.applyStyle(node, spec.style)
  }

  private applyDataset(node: HTMLElement, dataset?: Record<string, string>): void {
    if (!dataset) {
      for (const key of Object.keys(node.dataset)) {
        delete node.dataset[key]
      }
      return
    }

    const nextKeys = new Set(Object.keys(dataset))
    for (const key of Object.keys(node.dataset)) {
      if (!nextKeys.has(key)) delete node.dataset[key]
    }

    for (const [key, value] of Object.entries(dataset)) {
      node.dataset[key] = value
    }
  }

  private applyAttrs(node: HTMLElement, attrs?: Record<string, string>): void {
    const prev = Array.from(node.attributes)
      .map(a => a.name)
      .filter(name => !name.startsWith('data-') && name !== 'id' && name !== 'class' && name !== 'style')

    const next = attrs ? new Set(Object.keys(attrs)) : new Set<string>()
    for (const name of prev) {
      if (!next.has(name)) node.removeAttribute(name)
    }

    if (!attrs) return
    for (const [name, value] of Object.entries(attrs)) {
      node.setAttribute(name, value)
    }
  }

  private applyStyle(node: HTMLElement, style?: Record<string, string>): void {
    node.removeAttribute('style')
    if (!style) return

    for (const [name, value] of Object.entries(style)) {
      node.style.setProperty(name, value)
    }
  }
}
